import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useDraftBuffer from '../hooks/useDraftBuffer';
import SaveStatus from '../components/common/SaveStatus';
import DraftRecoveryNotice from '../components/common/DraftRecoveryNotice';
import { useSearchParams } from 'react-router-dom';
import { correctivePlansApi, auditsApi, auditReportsApi, procedureControlApi, filesApi, getApiErrorMessage } from '../api';
import { useAuth } from '../store/AuthContext';
import { useConfirm, usePrompt } from '../store/ConfirmContext';
import { isAuditeeRole } from '../utils/permissions';
import ProcessStepper from '../components/common/ProcessStepper';
import Badge from '../components/ui/Badge';
import { CheckCircle2, Trash2 } from 'lucide-react';
import PageIntro from '../components/ui/PageIntro';
import CauseAnalysisEditor from '../components/audits/CauseAnalysisEditor';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const emptyItem = {
  id: null,
  issue_id: null,
  issue_title: '',
  description: '',
  root_cause: '',
  cause_key: null,
  requirement_ref: '',
  risk_level: 'minor',
  action_owner_name: '',
  action_owner_user_id: null,
  due_date: '',
  elimination_status: 'open',
  customer_status: 'draft',
  customer_comment: '',
  evidence_description: '',
  evidence_status: 'not_requested',
  verification_comment: '',
  note: '',
};

const defaultForm = () => ({
  id: null,
  audit_id: '',
  report_id: '',
  number: `ПКПД/${new Date().getFullYear()}`,
  status: 'open',
  responsible_name: '',
  due_date: '',
  items: [{ ...emptyItem }],
  cause_analysis: [],
});

function toDateInput(value) {
  return value ? String(value).slice(0, 10) : '';
}

function normalizePlanItem(item = {}) {
  return {
    ...emptyItem,
    ...item,
    issue_id: item.issue_id || null,
    action_owner_user_id: item.action_owner_user_id || null,
    due_date: toDateInput(item.due_date),
    customer_status: item.customer_status || 'draft',
    evidence_status: item.evidence_status || 'not_requested',
  };
}

const riskLabels = {
  major: uiText("Значительное (Major)"),
  ofi: uiText("Возможность для улучшения (OFI)"),
  sp: uiText("Сильная сторона (SP)"),
  critical: uiText("Критическое"),
  significant: uiText("Значительное"),
  minor: uiText("Незначительное"),
  recommendation: uiText("Рекомендация"),
};

const evidenceStatusLabels = {
  not_requested: uiText("Не запрошены (пока не требуются)"),
  planned: uiText("Готовятся исполнителем"),
  submitted: uiText("Переданы аудитору на проверку"),
  received: uiText("Приняты аудитором к проверке"),
  verified: uiText("Проверены и подтверждены аудитором"),
  rejected: uiText("Возвращены аудитором на доработку"),
};

const correctiveStateLabels = {
  not_required: uiText("Не требуется (замечаний нет)"),
  open: uiText("Черновик у проверяемой стороны"),
  draft: uiText("Черновик у проверяемой стороны"),
  submitted: uiText("Ожидает согласования аудитора"),
  approved: uiText("Согласован — можно выполнять"),
  changes_requested: uiText("Возвращён на доработку"),
  in_progress: uiText("В работе (мероприятия выполняются)"),
  verification: uiText("Проверяется аудитором"),
  customer_control: uiText("Передан заказчику для итогового контроля"),
  closed: uiText("Закрыт (все пункты подтверждены)"),
};

const verificationStateLabels = {
  pending: uiText("Ожидает передачи материалов"),
  in_review: uiText("Проверяется аудитором"),
  verified: uiText("Подтверждено аудитором"),
  rejected: uiText("Возвращено аудитором на доработку"),
  not_required: uiText("Не требуется (проверять нечего)"),
};

function statusLabel(map, value) {
  return map[value] || value || '—';
}

const actionStatusLabels = {
  open: uiText("Не начато"),
  in_progress: uiText("В работе"),
  submitted: uiText("Выполнено — ожидает проверки аудитора"),
  needs_revision: uiText("Возвращено на доработку"),
  rejected: uiText("Возвращено на доработку"),
  verified: uiText("Принято аудитором"),
  closed: uiText("Принято аудитором"),
  resolved: uiText("Принято аудитором"),
};

function buildItemsFromAudit(audit) {
  const issues = (audit?.issues || []).filter((issue) => !['verified', 'closed'].includes(issue.status) && (audit?.meta?.workflow_version !== 'compliance-v2' || ['major', 'minor'].includes(issue.priority)));
  if (!issues.length) return [{ ...emptyItem }];
  return issues.map((issue) => ({
    ...emptyItem,
    issue_id: issue.id,
    issue_title: issue.title || '',
    description: '',
    requirement_ref: issue.requirement_ref || (issue.question_id ? uiText("Вопрос #{v0}", {v0: (issue.question_id)}) : ''),
    risk_level: issue.classification || issue.priority || 'minor',
    action_owner_name: issue.assignee?.full_name || '',
    action_owner_user_id: issue.assignee?.id || null,
    due_date: toDateInput(issue.due_date),
    note: issue.description || '',
  }));
}

export default function CorrectivePlansPage() {
  const confirm = useConfirm();
  const prompt = usePrompt();
  const { user } = useAuth();
  const isCustomer = isAuditeeRole(user);
  const [searchParams] = useSearchParams();
  const requestedAuditId = searchParams.get('audit_id') || '';
  const prefillDoneRef = useRef(false);
  useEffect(() => { prefillDoneRef.current = false; }, [requestedAuditId]);
  const [plans, setPlans] = useState([]);
  const [audits, setAudits] = useState([]);
  const [reports, setReports] = useState([]);
  const [states, setStates] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [activePkpdFormStep, setActivePkpdFormStep] = useState('document');
  const formBuffer = useDraftBuffer(user?.id, requestedAuditId || 'registry', 'pkpd');
  const form = formBuffer.values.form || defaultForm();
  const setForm = value => formBuffer.edit('form', typeof value === 'function' ? value(formBuffer.values.form || defaultForm()) : value);
  const saveInFlight = useRef(null);
  const actionBusy = useRef(false);

  const load = async () => {
    setError('');
    const [plansRes, auditsRes, reportsRes, statesRes] = await Promise.allSettled([
      correctivePlansApi.list(),
      auditsApi.list({ detailed: true }),
      auditReportsApi.list(),
      procedureControlApi.list(),
    ]);

    const messages = [];
    if (plansRes.status === 'fulfilled') setPlans(Array.isArray(plansRes.value.data) ? plansRes.value.data : []);
    else {
      setPlans([]);
      messages.push(uiText("ПКПД: {v0}", {v0: (getApiErrorMessage(plansRes.reason))}));
    }
    if (auditsRes.status === 'fulfilled') setAudits(Array.isArray(auditsRes.value.data) ? auditsRes.value.data : []);
    else {
      setAudits([]);
      messages.push(uiText("Аудиты: {v0}", {v0: (getApiErrorMessage(auditsRes.reason))}));
    }
    if (reportsRes.status === 'fulfilled') setReports(Array.isArray(reportsRes.value.data) ? reportsRes.value.data : []);
    else {
      setReports([]);
      messages.push(uiText("Отчёты: {v0}", {v0: (getApiErrorMessage(reportsRes.reason))}));
    }
    if (statesRes.status === 'fulfilled') setStates(Array.isArray(statesRes.value.data) ? statesRes.value.data : []);
    else {
      setStates([]);
      messages.push(uiText("Контур процедуры: {v0}", {v0: (getApiErrorMessage(statesRes.reason))}));
    }
    if (messages.length) setError(messages.join(' · '));
  };

  useEffect(() => {
    load();
  }, []);

  const auditsById = useMemo(() => Object.fromEntries(audits.map((item) => [String(item.id), item])), [audits]);
  const stateByAudit = useMemo(() => Object.fromEntries(states.map((x) => [String(x.audit_id), x])), [states]);
  const reportsForAudit = useMemo(() => {
    if (!form.audit_id) return reports;
    return reports.filter((x) => String(x.audit_id) === String(form.audit_id));
  }, [reports, form.audit_id]);
  const selectedAudit = auditsById[String(form.audit_id)] || null;
  const modern = selectedAudit?.meta?.workflow_version === 'compliance-v2';
  const issuesForAudit = (selectedAudit?.issues || []).filter((issue) => modern ? ['major', 'minor'].includes(issue.priority) : !['verified', 'closed'].includes(issue.status));
  const planStatus = String(form.status || 'open').toLowerCase();
  const definitionEditable = ['open', 'draft', 'changes_requested'].includes(planStatus);
  const executionEnabled = ['approved', 'in_progress'].includes(planStatus);
  const awaitingPlanReview = planStatus === 'submitted';
  const isStaff = !isCustomer;
  const internalNoRepresentative = Boolean(
    isStaff
    && selectedAudit
    && !selectedAudit.customer
    && ['aas', 'auditor', 'none'].includes(String(selectedAudit.meta?.control_mode || '').toLowerCase()),
  );
  const canRecordExecution = isCustomer || internalNoRepresentative;

  const auditTitle = (auditId) => audits.find((a) => Number(a.id) === Number(auditId))?.title || uiText("Аудит #{v0}", {v0: (auditId)});

  const resetForm = () => {
    formBuffer.hydrate({ form: defaultForm() });
  };

  const editPlan = (plan) => {
    const audit = auditsById[String(plan.audit_id)];
    formBuffer.hydrate({ form: {
      id: plan.id,
      audit_id: plan.audit_id ? String(plan.audit_id) : '',
      report_id: plan.report_id ? String(plan.report_id) : '',
      number: plan.number || `ПКПД/${new Date().getFullYear()}`,
      status: plan.status || 'open',
      cause_analysis: plan.cause_analysis || [],
      responsible_name: plan.responsible_name || '',
      due_date: toDateInput(plan.due_date),
      items: plan.items?.length ? plan.items.map(normalizePlanItem) : buildItemsFromAudit(audit),
    } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!requestedAuditId || prefillDoneRef.current || !audits.length) return;
    prefillDoneRef.current = true;
    const existing = plans.find((item) => String(item.audit_id) === String(requestedAuditId));
    if (existing) {
      editPlan(existing);
      return;
    }
    const audit = auditsById[String(requestedAuditId)];
    const report = reports.find((item) => String(item.audit_id) === String(requestedAuditId));
    formBuffer.hydrate({ form: {
      ...defaultForm(),
      audit_id: String(requestedAuditId),
      report_id: report?.id ? String(report.id) : '',
      responsible_name: audit?.meta?.audited_company?.contact_person || '',
      items: buildItemsFromAudit(audit),
    } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [requestedAuditId, audits.length, plans, reports, auditsById]);

  const fillItemsFromAudit = async () => {
    const audit = auditsById[String(form.audit_id)];
    if (!audit) return;
    if (!await confirm(uiText("Список действий будет заменён пунктами из замечаний. Введённые в текущем списке правки будут потеряны."), { title: uiText("Заполнить из замечаний?"), confirmLabel: uiText("Заменить список") })) return;
    setForm((prev) => ({ ...prev, items: buildItemsFromAudit(audit) }));
  };

  const openPlan = async plan => {
    if (saving || actionBusy.current) return;
    if ((formBuffer.count || formBuffer.recovery) && !await confirm(uiText("В форме есть несохранённые правки. Перейти к другому ПКПД без их сохранения?"), { title: uiText("Несохранённый ПКПД"), confirmLabel: uiText("Перейти без сохранения") })) return;
    formBuffer.discard();
    if (plan) editPlan(plan); else resetForm();
  };

  const handleAuditChange = (auditId) => {
    const audit = auditsById[String(auditId)];
    const report = reports.find((item) => String(item.audit_id) === String(auditId));
    setForm((prev) => ({
      ...prev,
      audit_id: auditId,
      report_id: report?.id ? String(report.id) : '',
      items: prev.items.length === 1 && !prev.items[0].description ? buildItemsFromAudit(audit) : prev.items,
    }));
  };

  const exportPdf = async (item) => {
    try {
      const res = await correctivePlansApi.pdf(item.id);
      downloadBlob(res.data, `${item.number || `corrective_plan_${item.id}`}.pdf`);
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  const updateItem = (idx, field, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === idx ? { ...item, [field]: value } : item)),
    }));
  };

  const updateIssueLink = (idx, issueId) => {
    const issue = issuesForAudit.find((row) => String(row.id) === String(issueId));
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === idx ? {
        ...item,
        issue_id: issue?.id || null,
        cause_key: null,
        issue_title: issue?.title || '',
        requirement_ref: issue?.requirement_ref || (issue?.question_id ? uiText("Вопрос #{v0}", {v0: (issue.question_id)}) : ''),
        risk_level: issue?.classification || issue?.priority || 'minor',
      } : item)),
    }));
  };

  const addItem = () => {
    const firstIssue = issuesForAudit[0];
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, {
        ...emptyItem,
        issue_id: firstIssue?.id || null,
        issue_title: firstIssue?.title || '',
        requirement_ref: firstIssue?.requirement_ref || '',
        risk_level: firstIssue?.classification || firstIssue?.priority || 'minor',
      }],
    }));
  };

  const removeItem = (idx) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== idx) : [{ ...emptyItem }],
    }));
  };

  const buildPayload = () => ({
    ...form,
    cause_analysis: modern && definitionEditable ? form.cause_analysis || [] : undefined,
    audit_id: Number(form.audit_id),
    report_id: form.report_id ? Number(form.report_id) : null,
    due_date: form.due_date || null,
    items: form.items.map((item) => ({
      ...item,
      id: item.id || null,
      issue_id: item.issue_id || null,
      action_owner_user_id: item.action_owner_user_id || null,
      due_date: item.due_date || null,
    })),
  });

  const savePlan = async ({ reset = true } = {}) => {
    if (saveInFlight.current) return saveInFlight.current;
    if (formBuffer.recovery) { setError(uiText("Сначала восстановите черновик или оставьте данные сервера.")); return null; }
    setSaving(true);
    setError('');
    const snapshot = formBuffer.snapshot();
    saveInFlight.current = (async () => { try {
      const response = await correctivePlansApi.upsert(buildPayload());
      formBuffer.acknowledge(snapshot, { committed: true });
      if (reset) resetForm();
      else editPlan(response.data);
      await load();
      return response.data;
    } catch (e2) {
      setError(getApiErrorMessage(e2));
      return null;
    } finally {
      saveInFlight.current = null;
      setSaving(false);
    } })();
    return saveInFlight.current;
  };

  const submit = async (e) => {
    e.preventDefault();
    await savePlan();
  };

  const submitForReview = async () => {
    if (actionBusy.current || saving) return;
    actionBusy.current = true;
    const saved = await savePlan({ reset: false });
    if (!saved) { actionBusy.current = false; return; }
    setSaving(true);
    try {
      const response = await correctivePlansApi.submit(saved.id);
      editPlan(response.data);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      actionBusy.current = false;
      setSaving(false);
    }
  };

  const reviewPlan = async (approved) => {
    if (actionBusy.current || saving) return;
    if (formBuffer.count || formBuffer.recovery) { setError(uiText("Сохраните изменения ПКПД перед проверкой плана.")); return; }
    actionBusy.current = true;
    const comment = approved
      ? ''
      : await prompt(uiText("Опишите, что проверяемой стороне нужно исправить в плане."), {
          title: uiText("Вернуть план на доработку"),
          confirmLabel: uiText("Вернуть на доработку"),
          placeholder: uiText("Комментарий аудитора"),
          required: true,
        });
    if (!approved && comment === null) { actionBusy.current = false; return; }
    setSaving(true);
    setError('');
    try {
      const response = await correctivePlansApi.review(form.id, { approved, comment });
      editPlan(response.data);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      actionBusy.current = false;
      setSaving(false);
    }
  };

  const reviewAction = async (item, approved) => {
    if (actionBusy.current || saving) return;
    if (formBuffer.count || formBuffer.recovery) { setError(uiText("Сохраните изменения ПКПД перед проверкой действия.")); return; }
    actionBusy.current = true;
    const comment = await prompt(
      approved
        ? uiText("При необходимости добавьте комментарий к результату проверки.")
        : uiText("Опишите, почему корректирующее действие нужно доработать."),
      {
        title: approved ? uiText("Принять выполнение") : uiText("Вернуть действие на доработку"),
        confirmLabel: approved ? uiText("Принять действие") : uiText("Вернуть на доработку"),
        placeholder: uiText("Комментарий аудитора"),
        required: !approved,
      },
    );
    if (comment === null) { actionBusy.current = false; return; }
    setSaving(true);
    setError('');
    try {
      const response = await correctivePlansApi.reviewItem(form.id, item.id, { approved, comment: comment || '' });
      editPlan(response.data);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      actionBusy.current = false;
      setSaving(false);
    }
  };

  const uploadEvidence = async (idx, file) => {
    const item = form.items[idx];
    if (!file || !item?.id || !item.issue_id) return;
    setUploadingItemId(item.id);
    setError('');
    try {
      const response = await filesApi.upload({
        auditId: Number(form.audit_id),
        issueId: item.issue_id,
        purpose: `corrective_action_${item.id}`,
        file,
      });
      updateItem(idx, 'evidence_files', [...(item.evidence_files || []), response.data]);
      if (item.evidence_status === 'not_requested') updateItem(idx, 'evidence_status', 'planned');
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setUploadingItemId(null);
    }
  };

  const downloadEvidence = async (file) => {
    try {
      const response = await filesApi.download(file.id);
      downloadBlob(response.data, file.original_name || file.filename || `file_${file.id}`);
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  const deleteEvidence = async (idx, file) => {
    if (!file?.id) return;
    const fileName = file.original_name || file.filename || uiText("Файл #{v0}", {v0: (file.id)});
    const approved = await confirm(
      uiText("Удалить «{v0}» из подтверждающих материалов? Запись об удалении останется в журнале аудита.", {v0: (fileName)}),
      { title: uiText("Удаление подтверждающего файла"), confirmLabel: uiText("Удалить файл"), danger: true },
    );
    if (!approved) return;
    setDeletingFileId(file.id);
    setError('');
    try {
      await filesApi.delete(file.id);
      const currentFiles = form.items[idx]?.evidence_files || [];
      updateItem(idx, 'evidence_files', currentFiles.filter((item) => item.id !== file.id));
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setDeletingFileId(null);
    }
  };

  const summarizeItems = (items = []) => {
    const total = items.length;
    const verified = items.filter((x) => x.evidence_status === 'verified' || x.evidence_verified_at).length;
    const waiting = items.filter((x) => x.elimination_status === 'submitted').length;
    return uiText("{v0} действ., принято аудитором {v1}{v2}", {v0: (total), v1: (verified), v2: (waiting ? `, ожидают проверки ${waiting}` : '')});
  };

  const pkpdProcessIndex = (() => {
    if (!form.audit_id) return 0;
    if (['closed', 'completed', 'verified'].includes(form.status)) return 5;
    if (['verification', 'on_verification', 'partially_verified'].includes(form.status)) return 4;
    if (['approved', 'in_progress'].includes(form.status)) return 3;
    if (form.status === 'submitted') return 2;
    return 1;
  })();
  const pkpdFocus = (() => {
    if (!form.audit_id) return { title: uiText("Выберите аудит"), text: uiText("После выбора система подгрузит замечания и доступные документы."), actor: uiText("Аудитор или администратор") };
    if (['open', 'draft', 'changes_requested'].includes(form.status)) return { title: uiText("Подготовить план корректирующих действий"), text: uiText("Добавьте одно или несколько действий к каждому замечанию и передайте план на согласование."), actor: internalNoRepresentative ? uiText("Команда аудита") : uiText("Проверяемая сторона") };
    if (form.status === 'submitted') return { title: uiText("Проверить и согласовать план"), text: uiText("После согласования проверяемая сторона сможет перейти к исполнению действий."), actor: uiText("Аудитор") };
    if (['approved', 'in_progress'].includes(form.status)) return { title: uiText("Выполнить действия и приложить подтверждения"), text: internalNoRepresentative ? uiText("Зафиксируйте полученные вне системы подтверждения и передайте каждое действие на проверку.") : uiText("Каждое выполненное действие отдельно передаётся аудитору на проверку."), actor: internalNoRepresentative ? uiText("Команда аудита") : uiText("Проверяемая сторона") };
    if (['verification', 'on_verification', 'partially_verified'].includes(form.status)) return { title: uiText("Проверить подтверждающие материалы"), text: uiText("Примите выполненные действия или верните конкретные пункты на доработку."), actor: uiText("Аудитор") };
    return { title: uiText("ПКПД закрыт"), text: uiText("Все корректирующие действия приняты. Документ остаётся доступным в истории."), actor: uiText("Система") };
  })();
  const pkpdSteps = [
    { key: 'issue', label: uiText("Замечание"), onClick: () => document.getElementById('pkpd-form-card')?.scrollIntoView({ behavior: 'smooth' }) },
    { key: 'plan', label: uiText("План действий"), onClick: () => document.getElementById('pkpd-actions')?.scrollIntoView({ behavior: 'smooth' }) },
    { key: 'approval', label: uiText("Согласование"), onClick: () => document.getElementById('pkpd-actions')?.scrollIntoView({ behavior: 'smooth' }) },
    { key: 'execution', label: uiText("Исполнение"), onClick: () => document.getElementById('pkpd-actions')?.scrollIntoView({ behavior: 'smooth' }) },
    { key: 'verification', label: uiText("Проверка выполнения КД"), onClick: () => document.getElementById('pkpd-actions')?.scrollIntoView({ behavior: 'smooth' }) },
    { key: 'closure', label: uiText("Завершение ПКПД"), onClick: () => document.getElementById('pkpd-registry')?.scrollIntoView({ behavior: 'smooth' }) },
  ];
  const pkpdFormSteps = [
    { key: 'document', label: uiText("Документ и ответственные"), complete: Boolean(form.audit_id && form.number) },
    {
      key: 'actions',
      label: uiText("Корректирующие действия"),
      complete: Boolean(form.items.length && form.items.every((item) => item.issue_id && item.description)),
    },
    {
      key: 'review',
      label: uiText("Проверка и передача"),
      complete: Boolean(form.audit_id && form.items.length && form.items.every((item) => item.issue_id && item.description)),
    },
  ];
  const goToPkpdFormStep = (key) => {
    setActivePkpdFormStep(key);
    const target = key === 'document' ? 'pkpd-document-fields' : key === 'actions' ? 'pkpd-actions' : 'pkpd-review-actions';
    document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="corrective-plans-page">
      <PageIntro
        icon={CheckCircle2}
        title={uiText("План корректирующих действий")}
        description={internalNoRepresentative
          ? uiText("В аудите без представителя команда аудита фиксирует план и полученные вне системы подтверждения; согласование и приёмка каждого действия остаются под контролем аудитора.")
          : uiText("Проверяемая сторона разрабатывает план, аудитор согласует его к исполнению, затем отдельно принимает каждое выполненное действие по подтверждающим материалам.")}
      />

      <div className="pkpd-status-guide" role="note">
        <strong>{uiText("Маршрут ПКПД:")}</strong>
        <span>{uiText("1. Аудитор направляет замечание")}</span>
        <span>2. {internalNoRepresentative ? uiText("Команда аудита фиксирует полученный план из одного или нескольких действий") : uiText("Проверяемый составляет план из одного или нескольких действий")}</span>
        <span>{uiText("3. Аудитор согласует план: «можно выполнять»")}</span>
        <span>4. {internalNoRepresentative ? uiText("Команда аудита регистрирует полученные подтверждения выполнения") : uiText("Проверяемый выполняет действия и прикладывает подтверждения")}</span>
        <span>{uiText("5. Аудитор принимает каждое действие либо возвращает его")}</span>
        <span>{uiText("6. Документ закрывается автоматически после приёмки всех действий")}</span>
      </div>

      <div className="workflow-focus-card">
        <div className="workflow-focus-main">
          <div className="workflow-focus-eyebrow">{uiText("Текущее обязательное действие")}</div>
          <div className="workflow-focus-title">{pkpdFocus.title}</div>
          <div className="workflow-focus-meta">
            <Badge variant={pkpdProcessIndex >= 5 ? 'success' : pkpdProcessIndex === 2 || pkpdProcessIndex === 4 ? 'warning' : 'info'}>
              {statusLabel(correctiveStateLabels, form.status)}
            </Badge>
            <span className="workspace-muted">{uiText("Сейчас действует: ")}{pkpdFocus.actor}</span>
          </div>
          <div className="workspace-muted">{pkpdFocus.text}</div>
        </div>
      </div>

      <ProcessStepper steps={pkpdSteps} currentIndex={pkpdProcessIndex} ariaLabel={uiText("Этапы ПКПД")} />

      {error ? <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div> : null}

      <div className="card" id="pkpd-form-card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div className="card-title">{form.id ? uiText("Редактирование ПКПД") : uiText("Новый ПКПД")}</div>
          {form.id && !isCustomer ? <button type="button" className="ap-button ap-button--secondary ap-button--sm" disabled={saving} onClick={() => openPlan(null)}>{uiText("Новый ПКПД")}</button> : null}
        </div>
        <div className="card-body">
          <DraftRecoveryNotice buffer={formBuffer} label={uiText("ПКПД")} disabled={saving} />
          <SaveStatus saving={saving} error={error} dirty={formBuffer.count>0} recovery={formBuffer.recovery}/>
          {formBuffer.count > 0 && <div className="ap-save-notice" role="status"><div><strong>{uiText("Есть несохранённые изменения ПКПД")}</strong><p>{uiText("Черновик сохраняется в этой вкладке. Для передачи на сервер используйте кнопку сохранения ниже.")}</p></div></div>}
          <div className="form-stepper" aria-label={uiText("Этапы заполнения ПКПД")}>
            {pkpdFormSteps.map((step, index) => (
              <button
                key={step.key}
                type="button"
                className={`form-stepper-button ${activePkpdFormStep === step.key ? 'is-active' : ''} ${step.complete ? 'is-complete' : ''}`}
                onClick={() => goToPkpdFormStep(step.key)}
                aria-current={activePkpdFormStep === step.key ? 'step' : undefined}
              >
                <span className="form-stepper-number">{step.complete ? <CheckCircle2 size={13} /> : index + 1}</span>
                {step.label}
              </button>
            ))}
          </div>
          <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
            <fieldset className="ap-form-lock" disabled={saving || !!formBuffer.recovery}>
            <div className="form-row" id="pkpd-document-fields" style={{ scrollMarginTop: 76 }}>
              <div className="form-group">
                <label className="form-label">{uiText("Аудит")}</label>
                <select
                  className="form-control"
                  value={form.audit_id}
                  onChange={(e) => handleAuditChange(e.target.value)}
                  disabled={isCustomer || Boolean(form.id)}
                  required
                >
                  <option value="">{uiText("Выберите аудит...")}</option>
                  {audits.map((item) => <option key={item.id} value={item.id}>{item.title || uiText("Аудит #{v0}", {v0: (item.id)})}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{uiText("Отчёт / акт")}</label>
                <select className="form-control" value={form.report_id} onChange={(e) => setForm({ ...form, report_id: e.target.value })} disabled={isCustomer}>
                  <option value="">{uiText("Не выбран")}</option>
                  {reportsForAudit.map((item) => <option key={item.id} value={item.id}>{item.report_number || uiText("Отчёт #{v0}", {v0: (item.id)})}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{uiText("Номер ПКПД")}</label>
                <input className="form-control" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} disabled={isCustomer} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{uiText("Статус документа (меняется автоматически)")}</label>
                <div className="form-control pkpd-readonly-status">
                  {statusLabel(correctiveStateLabels, form.status)}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{internalNoRepresentative ? uiText("Ответственный / внешний координатор (если известен)") : uiText("Координатор ПКПД со стороны проверяемой организации")}</label>
                <input className="ap-control" name="responsible_name" aria-label={uiText("Координатор ПКПД")} value={form.responsible_name} readOnly={!definitionEditable} onChange={(e) => setForm({ ...form, responsible_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{uiText("Общий срок")}</label>
                <input className="form-control" type="date" value={form.due_date} readOnly={!definitionEditable} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>

            {modern && <CauseAnalysisEditor issues={selectedAudit?.issues || []} value={form.cause_analysis || []} readOnly={!definitionEditable}
              onChange={cause_analysis => setForm(prev => ({ ...prev, cause_analysis,
                items: prev.items.map(item => ({ ...item, cause_key: cause_analysis.some(row => row.issue_id === item.issue_id && row.causes.some(c => c.key === item.cause_key)) ? item.cause_key : null })) }))} />}
            <div className="card" id="pkpd-actions" style={{ border: '1px solid var(--border-color, #475569)' }}>
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div className="card-title">{uiText("Несоответствия и корректирующие действия")}</div>
                {definitionEditable ? <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-outline" onClick={fillItemsFromAudit} disabled={!form.audit_id}>{uiText("Заполнить из замечаний аудита")}</button>
                  <button type="button" className="btn btn-outline" onClick={addItem}>{uiText("Добавить корректирующее действие")}</button>
                </div> : null}
              </div>
              <div className="card-body" style={{ display: 'grid', gap: 12 }}>
                {form.items.map((item, idx) => (
                  <div key={item.id || idx} className="card" style={{ border: '1px dashed #ddd' }}>
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <div className="card-title">{uiText("Корректирующее действие #")}{idx + 1}{item.issue_title ? ` · ${item.issue_title}` : ''}</div>
                      {definitionEditable ? <button type="button" className="btn btn-outline btn-sm" onClick={() => removeItem(idx)}>{uiText("Удалить")}</button> : null}
                    </div>
                    <div className="card-body" style={{ display: 'grid', gap: 10 }}>
                      <div className="form-group">
                        <label className="form-label">{uiText("Замечание, к которому относится действие")}</label>
                        <select
                          className="form-control"
                          value={item.issue_id || ''}
                          disabled={!definitionEditable}
                          onChange={(e) => updateIssueLink(idx, e.target.value)}
                          required={!modern}
                        >
                          <option value="">{uiText("Выберите замечание...")}</option>
                          {issuesForAudit.map((issue) => (
                            <option key={issue.id} value={issue.id}>{issue.title || uiText("Замечание #{v0}", {v0: (issue.id)})}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">{uiText("Причина несоответствия (почему оно возникло)")}</label>
                        {modern ? <select className="ap-control" value={item.cause_key || ''} disabled={!definitionEditable}
                          onChange={event => updateItem(idx, 'cause_key', event.target.value || null)}>
                          <option value="">{uiText("Выберите причину из анализа выше")}</option>
                          {(form.cause_analysis || []).find(row => row.issue_id === item.issue_id)?.causes.map((cause, index) =>
                            <option key={cause.key} value={cause.key}>{cause.text || uiText("Причина {v0} — описание не заполнено", {v0: (index + 1)})}</option>)}
                        </select> : <textarea className="form-control" rows={2} value={item.root_cause} readOnly={!definitionEditable} onChange={(e) => updateItem(idx, 'root_cause', e.target.value)} />}
                      </div>
                      <div className="form-group">
                        <label className="form-label">{uiText("Корректирующее действие (что нужно сделать)")}</label>
                        <textarea className="form-control" rows={2} value={item.description} readOnly={!definitionEditable} onChange={(e) => updateItem(idx, 'description', e.target.value)} required={!modern && canRecordExecution} />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">{uiText("Требование / ссылка")}</label>
                          <input
                            className="form-control"
                            value={item.requirement_ref}
                            readOnly={!!item.issue_id || isCustomer}
                            style={(item.issue_id || isCustomer) ? { background: '#fafafa', color: '#475569' } : undefined}
                            onChange={(e) => updateItem(idx, 'requirement_ref', e.target.value)}
                          />
                          {item.issue_id ? <div style={{ fontSize: 12, color: 'var(--premium-text-secondary)', marginTop: 4 }}>{uiText("Берётся из карточки замечания и меняется аудитором в замечании.")}</div> : null}
                        </div>
                        <div className="form-group">
                          <label className="form-label">{uiText("Класс несоответствия")}</label>
                          <select className="form-control" value={item.risk_level} disabled={modern || !definitionEditable || isCustomer} onChange={(e) => updateItem(idx, 'risk_level', e.target.value)}>
                            {modern && <option value="major">{uiText("Значительное (Major)")}</option>}
                            <option value="critical">{uiText("Критическое")}</option>
                            <option value="significant">{uiText("Значительное")}</option>
                            <option value="minor">{uiText("Незначительное")}</option>
                            <option value="recommendation">{uiText("Рекомендация")}</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">{uiText("Исполнитель корректирующего действия")}</label>
                          <input className="form-control" value={item.action_owner_name} readOnly={!definitionEditable} onChange={(e) => updateItem(idx, 'action_owner_name', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{uiText("Срок КД")}</label>
                          <input className="form-control" type="date" value={item.due_date} readOnly={!definitionEditable} onChange={(e) => updateItem(idx, 'due_date', e.target.value)} />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">{uiText("Статус выполнения")}</label>
                          <select
                            className="form-control"
                            value={item.elimination_status}
                            disabled={!canRecordExecution || !executionEnabled}
                            onChange={(e) => updateItem(idx, 'elimination_status', e.target.value)}
                          >
                            {!['open', 'in_progress', 'submitted', 'needs_revision'].includes(item.elimination_status) ? (
                              <option value={item.elimination_status}>{statusLabel(actionStatusLabels, item.elimination_status)}</option>
                            ) : null}
                            <option value="open">{uiText("Открыто (работы не начаты)")}</option>
                            <option value="in_progress">{uiText("В работе (исполнитель выполняет действие)")}</option>
                            <option value="submitted">{uiText("Выполнено — передать аудитору на проверку")}</option>
                            <option value="needs_revision">{uiText("Возвращено аудитором — требуется доработка")}</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">{uiText("Результат проверки аудитора")}</label>
                          <div className="form-control pkpd-readonly-status">
                            {statusLabel(actionStatusLabels, item.elimination_status)}
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">{uiText("Статус подтверждающих материалов")}</label>
                          <div className="form-control pkpd-readonly-status">
                            {statusLabel(evidenceStatusLabels, item.evidence_status)}
                          </div>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">{internalNoRepresentative ? uiText("Комментарий по исполнению") : uiText("Комментарий проверяемого")}</label>
                          <textarea className="form-control" rows={2} value={item.customer_comment} readOnly={!canRecordExecution || (!definitionEditable && !executionEnabled)} onChange={(e) => updateItem(idx, 'customer_comment', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{uiText("Описание доказательств / отчёт о выполнении")}</label>
                          <textarea className="form-control" rows={2} value={item.evidence_description} readOnly={!canRecordExecution || !executionEnabled} onChange={(e) => updateItem(idx, 'evidence_description', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{uiText("Комментарий аудитора по результату проверки")}</label>
                          <textarea className="form-control" rows={2} value={item.verification_comment} readOnly />
                        </div>
                      </div>
                      <div className="pkpd-evidence-box">
                        <div>
                          <strong>{uiText("Подтверждающие файлы")}</strong>
                          <div className="field-hint">{uiText("Приложите акт, фото, документ или другой материал, подтверждающий выполнение именно этого действия.")}</div>
                        </div>
                        <div className="pkpd-evidence-files">
                          {(item.evidence_files || []).map((file) => (
                            <span key={file.id} className="pkpd-evidence-file-item">
                              <button type="button" className="btn btn-outline btn-sm" onClick={() => downloadEvidence(file)}>
                                {file.original_name || file.filename}
                              </button>
                              {(
                                (isCustomer && executionEnabled && Number(file.uploaded_by_id) === Number(user?.id))
                                || (isStaff && ['open', 'draft', 'changes_requested', 'approved', 'in_progress'].includes(planStatus))
                              ) ? (
                                <button
                                  type="button"
                                  className="btn btn-outline btn-sm attachment-delete-button"
                                  onClick={() => deleteEvidence(idx, file)}
                                  disabled={deletingFileId === file.id}
                                  aria-label={uiText("Удалить файл {v0}", {v0: (file.original_name || file.filename || '')})}
                                  title={uiText("Удалить ошибочно загруженный файл")}
                                >
                                  <Trash2 size={14} /> {deletingFileId === file.id ? uiText("Удаление…") : uiText("Удалить")}
                                </button>
                              ) : null}
                            </span>
                          ))}
                          {!(item.evidence_files || []).length ? <span className="muted">{uiText("Файлы не приложены")}</span> : null}
                        </div>
                        {canRecordExecution && executionEnabled && item.id ? (
                          <label className="btn btn-outline btn-sm pkpd-file-button">
                            {uploadingItemId === item.id ? uiText("Загрузка...") : uiText("Приложить файл")}
                            <input
                              type="file"
                              hidden
                              disabled={uploadingItemId === item.id}
                              onChange={(e) => {
                                uploadEvidence(idx, e.target.files?.[0]);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        ) : null}
                        {canRecordExecution && executionEnabled && !item.id ? (
                          <span className="field-hint">{uiText("Сначала сохраните новое действие, затем приложите файл.")}</span>
                        ) : null}
                      </div>
                      {isStaff && item.elimination_status === 'submitted' ? (
                        <div className="pkpd-review-actions">
                          <strong>{uiText("Проверка выполненного действия")}</strong>
                          <button type="button" className="btn btn-primary btn-sm" onClick={() => reviewAction(item, true)} disabled={saving}>{uiText("Принять выполнение")}</button>
                          <button type="button" className="btn btn-outline btn-sm" onClick={() => reviewAction(item, false)} disabled={saving}>{uiText("Вернуть на доработку")}</button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {error && <div className="ap-save-notice ap-save-notice--warning" role="status"><strong>{error}</strong></div>}
            <div className="pkpd-form-actions" id="pkpd-review-actions" style={{ scrollMarginTop: 76 }}>
              {isStaff && awaitingPlanReview ? (
                <>
                  <button className="btn btn-primary" type="button" onClick={() => reviewPlan(true)} disabled={saving}>{uiText("Согласовать — можно выполнять")}</button>
                  <button className="btn btn-outline" type="button" onClick={() => reviewPlan(false)} disabled={saving}>{uiText("Вернуть план на доработку")}</button>
                </>
              ) : null}
              {definitionEditable || (canRecordExecution && executionEnabled) ? (
                <button className="btn btn-outline" type="submit" disabled={saving || (canRecordExecution && executionEnabled && !form.id)}>
                  {saving ? uiText("Сохранение...") : (executionEnabled ? uiText("Сохранить ход выполнения") : uiText("Сохранить черновик"))}
                </button>
              ) : null}
              {(isCustomer || internalNoRepresentative) && definitionEditable && form.id ? (
                <button className="btn btn-primary" type="button" onClick={submitForReview} disabled={saving}>
                  {internalNoRepresentative ? uiText("Зафиксировать план и передать на согласование") : uiText("Передать план аудитору на согласование")}
                </button>
              ) : null}
            </div>
            </fieldset>
          </form>
        </div>
      </div>

      <div className="card" id="pkpd-registry">
        <div className="card-header"><div className="card-title">{uiText("Реестр ПКПД")}</div></div>
        <div className="card-body table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{uiText("Номер")}</th>
                <th>{uiText("Аудит")}</th>
                <th>{uiText("Пункты")}</th>
                <th>{uiText("Маршрут закрытия")}</th>
                <th>{uiText("Координатор ПКПД")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {plans.length ? plans.map((item) => {
                const state = stateByAudit[String(item.audit_id)] || null;
                return (
                  <tr key={item.id}>
                    <td>{item.number || uiText("ПКПД #{v0}", {v0: (item.id)})}</td>
                    <td>{auditTitle(item.audit_id)}</td>
                    <td>
                      <div>{summarizeItems(item.items || [])}</div>
                      <div style={{ fontSize: 12, color: 'var(--premium-text-secondary)' }}>
                        {(item.items || []).slice(0, 2).map((child) => (
                          <div key={child.id || child.description}>
                            {statusLabel(riskLabels, child.risk_level)} · {statusLabel(actionStatusLabels, child.elimination_status)} · {statusLabel(evidenceStatusLabels, child.evidence_status)}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div>{statusLabel(correctiveStateLabels, item.status)}</div>
                      <div style={{ fontSize: 12, color: 'var(--premium-text-secondary)', marginTop: 4 }}>{uiText("Контроль действий: ")}{statusLabel(verificationStateLabels, state?.verification_status)}
                      </div>
                    </td>
                    <td>{item.responsible_name || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button className="ap-button ap-button--secondary ap-button--sm" type="button" disabled={saving} onClick={() => openPlan(item)}>{uiText("Открыть")}</button>
                        <button className="btn btn-outline btn-sm" type="button" onClick={() => exportPdf(item)}>PDF</button>
                      </div>
                    </td>
                  </tr>
                );
              }) : <tr><td colSpan={6}>{uiText("Пока нет планов корректирующих действий.")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
